import { NgClass } from '@angular/common';
import { Component, OnDestroy, OnInit, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { environment } from '@env/environment';
import { Store } from '@ngrx/store';
import { filter, map, startWith } from 'rxjs';

import { AppRoutes } from '@gfe/app.paths';
import { SidebarComponent } from '@gfe/core/components/sidebar/sidebar.component';
import { GlobalShortcutsService } from '@gfe/core/services/shortcuts.service';
import { SearchPopComponent } from '@gfe/shared/components/search-pop/search-pop.component';

import { AppShellBaseComponent } from '@core/components/app-shell-base/app-shell-base.component';
import { SidebarItemWithLabelKey } from '@core/components/sidebar/sidebar.model';
import { UnsavedChangesDialogComponent } from '@core/components/unsaved-changes-dialog/unsaved-changes-dialog.component';
import { KNOWN_FEATURE_FLAGS } from '@core/models/feature-flags/known-feature-flags';
import { canOpenOptimizations } from '@core/models/optimization-job-actions';
import { hasAdminOrAbove } from '@core/models/user-role.constants';
import { AssistantWindowHost } from '@core/services/assistant/assistant-window-host';
import { DataspaceRoutingService } from '@core/services/dataspace-routing/dataspace-routing.service';
import { DataspaceSelectionService } from '@core/services/dataspace-selection/dataspace-selection.service';
import { ICONS } from '@core/services/icons/icons.service';
import { SessionExpiryNoticeService } from '@core/services/session-expiry-notice/session-expiry-notice.service';
import { selectUser } from '@core/store/auth/auth.selectors';
import { selectDataspaceModel } from '@core/store/dataspace/dataspace.selectors';
import { selectIsFeatureActive } from '@core/store/feature-flags/feature-flags.selectors';
import {
  buildDataspaceUrl,
  extractDataspaceIdFromUrl
} from '@core/utils/dataspace-url.utils';

import { Shortcut } from '@shared/utils/shortcut';

import { PlanEditDialogsComponent } from '@features/components/plan/edit/plan-edit-dialogs/plan-edit-dialogs.component';
import { PlanEditFormOutletComponent } from '@features/components/plan/edit/plan-edit-forms/plan-edit-form-outlet.component';

@Component({
  selector: 'app-root-layout',
  imports: [
    AppShellBaseComponent,
    SidebarComponent,
    RouterOutlet,
    SearchPopComponent,
    NgClass,
    UnsavedChangesDialogComponent,
    PlanEditDialogsComponent,
    PlanEditFormOutletComponent
  ],
  templateUrl: './root-layout.component.html'
})
export class RootLayoutComponent implements OnInit, OnDestroy {
  isSearchPopupOpen = signal(false);

  private readonly globalShortcuts = inject(GlobalShortcutsService);
  private readonly dataspaceRoutingService = inject(DataspaceRoutingService);
  private readonly router = inject(Router);
  private readonly store = inject(Store);
  private readonly dataspaceSelection = inject(DataspaceSelectionService);
  private readonly sessionExpiryNotice = inject(SessionExpiryNoticeService);

  private readonly isProduction = environment.production;

  constructor() {
    // Only a tab lays out the app; the assistant's own window has no root layout, so it never starts the tab side.
    inject(AssistantWindowHost).start();
  }

  private readonly currentDataspace = this.store.selectSignal(selectDataspaceModel);
  private readonly currentUser = this.store.selectSignal(selectUser);
  private readonly isAdmin = computed(() => hasAdminOrAbove(this.currentUser()?.role));
  private readonly showOptimizationsFlag = this.store.selectSignal(
    selectIsFeatureActive(KNOWN_FEATURE_FLAGS.SHOW_OPTIMIZATIONS_MENU)
  );
  private readonly canOpenOptimizations = computed(() =>
    canOpenOptimizations(this.currentUser()?.role, this.showOptimizationsFlag())
  );

  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((e) => e instanceof NavigationEnd),
      map(() => this.router.url),
      startWith(this.router.url)
    )
  );

  readonly showDataspaceDropdown = computed(() =>
    (this.currentUrl() ?? '').startsWith('/ds/')
  );

  private getRoute(relativePath: string): string {
    const urlId = extractDataspaceIdFromUrl(this.router.url);
    if (urlId) {
      return buildDataspaceUrl(parseInt(urlId, 10), relativePath);
    }

    const selectedDataspaceId = this.dataspaceSelection.selectedId();
    if (selectedDataspaceId != null) {
      return buildDataspaceUrl(selectedDataspaceId, relativePath);
    }

    const storeDataspaceId = this.currentDataspace()?.id;
    if (storeDataspaceId) {
      return buildDataspaceUrl(storeDataspaceId, relativePath);
    }

    const defaultDataspaceId = this.currentUser()?.defaultDataSpace?.id;
    if (defaultDataspaceId) {
      return buildDataspaceUrl(defaultDataspaceId, relativePath);
    }

    return '';
  }

  readonly topSidebarItems = computed<SidebarItemWithLabelKey[]>(() => {
    // Trigger reactivity on URL changes so routes update when navigating
    this.currentUrl();
    return [
      {
        labelKey: 'sidebar.top.home',
        item: {
          iconClass: ICONS.house,
          route: this.getRoute(AppRoutes.home),
          isDisabled: false,
          routerLinkActiveOptions: { exact: true },
          shortcut: new Shortcut('Shift.h', () =>
            this.dataspaceRoutingService.navigateToPageInDataspaceOrFallbackToDefault(
              AppRoutes.home
            )
          )
        }
      },
      {
        labelKey: 'sidebar.top.data',
        item: {
          iconClass: ICONS.database,
          route: this.getRoute(AppRoutes.data.root),
          isDisabled: false,
          shortcut: new Shortcut('Shift.d', () =>
            this.dataspaceRoutingService.navigateToPageInDataspaceOrFallbackToDefault(
              AppRoutes.data.root
            )
          )
        }
      },
      {
        labelKey: 'sidebar.top.plan',
        item: {
          iconClass: ICONS.mapTrifold,
          route: this.getRoute(AppRoutes.plan.root),
          isDisabled: false,
          shortcut: new Shortcut('Shift.p', () =>
            this.dataspaceRoutingService.navigateToPageInDataspaceOrFallbackToDefault(
              AppRoutes.plan.root
            )
          )
        }
      },
      ...(this.canOpenOptimizations()
        ? [
            {
              labelKey: 'sidebar.top.optimizations',
              item: {
                iconClass: ICONS.gauge,
                route: `/${AppRoutes.optimizations.root}`,
                isDisabled: false
              }
            }
          ]
        : []),
      {
        labelKey: 'sidebar.top.solvers',
        item: {
          iconClass: ICONS.brain,
          route: this.getRoute(AppRoutes.solvers.root),
          isDisabled: this.isProduction,
          shortcut: new Shortcut('Shift.s', () =>
            this.isProduction
              ? null
              : this.dataspaceRoutingService.navigateToPageInDataspaceOrFallbackToDefault(
                  AppRoutes.solvers.root
                )
          )
        }
      },
      {
        labelKey: 'sidebar.top.reporting',
        item: {
          iconClass: ICONS.trendUp,
          route: this.getRoute(AppRoutes.reporting.root),
          isDisabled: this.isProduction,
          shortcut: new Shortcut('Shift.r', () =>
            this.isProduction
              ? null
              : this.dataspaceRoutingService.navigateToPageInDataspaceOrFallbackToDefault(
                  AppRoutes.reporting.root
                )
          )
        }
      }
    ];
  });

  readonly bottomSidebarItems = computed<SidebarItemWithLabelKey[]>(() => {
    this.currentUrl();
    return [
      {
        labelKey: 'sidebar.bottom.docs',
        item: {
          iconClass: ICONS.book,
          route: '',
          externalUrl: environment.docsUrl,
          isDisabled: !environment.docsUrl
        }
      },
      {
        labelKey: 'sidebar.bottom.support',
        item: {
          iconClass: ICONS.lifebuoy,
          route: '',
          externalUrl: environment.supportUrl,
          isDisabled: !environment.supportUrl
        }
      },
      ...(this.isAdmin()
        ? [
            {
              labelKey: 'sidebar.bottom.admin',
              item: {
                iconClass: ICONS.shield,
                route: `/${AppRoutes.admin.root}`,
                isDisabled: false,
                shortcut: new Shortcut('Shift.a', () =>
                  this.router.navigate([`/${AppRoutes.admin.root}`])
                )
              }
            }
          ]
        : []),
      {
        labelKey: 'sidebar.bottom.settings',
        item: {
          iconClass: ICONS.gear,
          route: `/${AppRoutes.profile.root}`,
          isDisabled: false,
          shortcut: new Shortcut('Shift.l', () =>
            this.router.navigate([`/${AppRoutes.profile.root}`])
          )
        }
      }
    ];
  });

  ngOnInit(): void {
    // If the user was just signed back in after their session expired, let
    // them know (and warn that unsaved changes may have been lost).
    this.sessionExpiryNotice.showIfExpired();

    const allItems = [...this.topSidebarItems(), ...this.bottomSidebarItems()];
    for (const item of allItems) {
      if (item.item.shortcut) {
        this.globalShortcuts.register(item.item.shortcut);
      }
    }
  }

  ngOnDestroy(): void {
    this.globalShortcuts.disposeAll();
  }
}
